import { useEffect, useState, useRef } from 'react';
import { MessageList } from './checklist/components/MessageList';
import { MessageInput } from './checklist/components/MessageInput';
import { useTenantDiscussionWebSocket } from '@/hooks/useTenantDiscussionWebSocket';
import { TenantDiscussionService } from '@/api/tenantDiscussion';
import { AlertCircle, WifiOff, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';

interface TenantDiscussionTabProps {
  tenantId: number;
  currentUser: {
    id: number;
    name: string;
    role: string;
  };
}

/**
 * TenantDiscussionTab - Discussion component without checklist instance dependency
 */
export const TenantDiscussionTab = ({ tenantId, currentUser }: TenantDiscussionTabProps) => {
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const lastReadIdRef = useRef<number | null>(null);
  const pageSize = 20;

  const {
    data: discussionData,
    isLoading: isLoadingMessages,
    error: fetchError,
  } = TenantDiscussionService.useGetDiscussions(tenantId, page, pageSize);

  useEffect(() => {
    if (fetchError) {
      console.error('[TenantDiscussionTab] Failed to fetch messages:', fetchError);
      toast.error('Gagal memuat pesan. Silakan segarkan halaman.');
    }
  }, [fetchError]);

  const {
    messages: wsMessages,
    isConnected,
    sendMessage,
    editMessage,
    deleteMessage,
    error: wsError,
    reconnect,
    setMessages,
  } = useTenantDiscussionWebSocket({
    tenantId,
    userId: currentUser.id,
    userName: currentUser.name,
    userRole: currentUser.role,
    enabled: true,
  });

  const markAsReadMutation = TenantDiscussionService.useMarkAsRead();

  useEffect(() => {
    if (discussionData?.items) {
      // BE returns DESC (newest first), reverse to ASC (oldest first) for consistent state
      const messagesInAsc = [...discussionData.items].reverse();

      if (page === 1) {
        setMessages(messagesInAsc);
      } else {
        setMessages(prev => [...messagesInAsc, ...prev]);
      }
      setIsLoadingMore(false);
    }
  }, [discussionData, page, setMessages]);

  useEffect(() => {
    if (wsMessages.length > 0 && tenantId && isConnected) {
      const lastMessage = wsMessages[wsMessages.length - 1];

      // Hanya panggil mark-read jika last message ID berubah
      if (lastMessage.id !== lastReadIdRef.current) {
        const timer = setTimeout(() => {
          markAsReadMutation.mutate({
            tenantId,
            payload: { last_read_id: lastMessage.id },
          });
          lastReadIdRef.current = lastMessage.id;
        }, 1000);

        return () => clearTimeout(timer);
      }
    }
  }, [wsMessages.length, tenantId, isConnected]);

  const handleSendMessage = (message: string) => {
    if (!isConnected) {
      toast.error('Tidak terhubung ke server chat. Silakan coba lagi.');
      return;
    }
    sendMessage(message);
  };

  const handleEditMessage = (discussionId: number, newMessage: string) => {
    if (!isConnected) {
      toast.error('Tidak terhubung ke server chat. Silakan coba lagi.');
      return;
    }
    editMessage(discussionId, newMessage);
  };

  const handleDeleteMessage = (discussionId: number) => {
    if (!isConnected) {
      toast.error('Tidak terhubung ke server chat. Silakan coba lagi.');
      return;
    }
    deleteMessage(discussionId);
  };

  const handleLoadMore = () => {
    if (!isLoadingMore) {
      setIsLoadingMore(true);
      setPage(prev => prev + 1);
    }
  };

  const hasMore = discussionData ? page < discussionData.total_page : false;

  if (isLoadingMessages && page === 1) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full dark:bg-gray-900">
      <div className="px-4 py-2 text-sm flex items-center gap-2 border-b border-gray-200 dark:border-gray-700">
        {isConnected ? (
          <>
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-gray-700 dark:text-gray-200">Terhubung ke chat</span>
          </>
        ) : (
          <>
            <WifiOff size={16} className="text-gray-500" />
            <span className="text-gray-700 dark:text-gray-200">Terputus dari chat</span>
            <button
              onClick={reconnect}
              className="ml-auto px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs"
            >
              Menghubungkan Ulang
            </button>
          </>
        )}
      </div>

      {wsError && !isConnected && (
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-sm flex items-center gap-2">
          <AlertCircle size={16} className="text-gray-500" />
          <span className="flex-1 text-gray-700 dark:text-gray-200">{wsError}</span>
          <button onClick={reconnect} className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-xs">
            Coba Lagi
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        <MessageList
          messages={wsMessages}
          currentUserId={currentUser.id}
          isLoading={isLoadingMessages}
          onEdit={handleEditMessage}
          onDelete={handleDeleteMessage}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
        />
      </div>

      <MessageInput
        onSend={handleSendMessage}
        disabled={!isConnected}
        placeholder={isConnected ? 'Ketik pesan...' : 'Menghubungkan ke chat...'}
      />
    </div>
  );
};
